import type { FC } from "react";

import type { DashboardAdminOrganization } from "../../dashboard-client";

import { formatUsd } from "../../utils/currency";
import { Badge } from "../common/Badge";
import { PanelHeaderBlock } from "../common/PanelHeaderBlock";
import { PanelSection } from "../common/PanelSection";
import { StatsGrid } from "../common/StatsGrid";
import { StatusBadge } from "../common/StatusBadge";
import { Text } from "../common/Text";
import { AdminOrgApiKeys } from "./AdminApiKeysTab";
import { AdminOrgCreditsPanel } from "./AdminOrgCreditsPanel";

interface AdminOrganizationWorkspaceProps {
  organization: DashboardAdminOrganization;
  onRefresh: () => Promise<void>;
}

export const AdminOrganizationWorkspace: FC<
  AdminOrganizationWorkspaceProps
> = ({ organization, onRefresh }) => (
  <PanelSection
    badge={<StatusBadge status={organization.status} size="sm" />}
    headerContent={
      <PanelHeaderBlock
        eyebrow="Workspace"
        title={organization.name}
        description="Manage balance and provider access for the currently selected organization."
        badge={
          <Badge variant="status" size="sm">
            {organization.plan_tier}
          </Badge>
        }
      />
    }
  >
    <div className="mb-4 space-y-3 rounded-none border border-slate-300 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-900/40">
      <StatsGrid
        columns={2}
        items={[
          {
            label: "Slug",
            value: <span className="font-mono">{organization.slug}</span>,
          },
          {
            label: "Members",
            value: (
              <span className="font-mono text-slate-900 dark:text-white">
                {organization.member_count}
              </span>
            ),
          },
          {
            label: "Credits",
            value: (
              <span className="font-mono text-slate-900 dark:text-white">
                {formatUsd(organization.credit_balance_usd)}
              </span>
            ),
          },
          {
            label: "Status",
            value: <StatusBadge status={organization.status} size="sm" />,
          },
        ]}
      />

      <Text as="p" className="leading-5">
        Credits and provider assignment changes here apply only to{" "}
        <span className="font-mono text-slate-900 dark:text-white">
          {organization.name}
        </span>
        .
      </Text>
    </div>

    <div className="space-y-4">
      <AdminOrgCreditsPanel
        orgId={organization.organization_id}
        currentBalanceUsd={organization.credit_balance_usd}
        onGranted={onRefresh}
      />
      <AdminOrgApiKeys orgId={organization.organization_id} />
    </div>
  </PanelSection>
);
